import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Check, Mic, X } from "lucide-react";
import PropTypes from "prop-types";
import { memo, useCallback, useEffect, useRef } from "react";
import { useVoiceDictation } from "../hooks/useVoiceDictation";
import { DictationVisualizer } from "./DictationVisualizer";

/**
 * Voice dictation button with recording interface
 * Uses Web Speech API for speech-to-text transcription
 * When recording, shows histogram with checkmark/X controls
 */
export const VoiceDictationButton = memo(function VoiceDictationButton({
  onTranscript,
  onConfirm,
  onCancel,
  disabled = false,
  className,
}) {
  const {
    isRecording,
    isSupported,
    fullTranscript,
    error,
    audioStream,
    startRecording,
    stopRecording,
    clearTranscript,
  } = useVoiceDictation();

  const lastTranscriptRef = useRef("");

  // Call onTranscript when transcript changes
  useEffect(() => {
    if (fullTranscript && fullTranscript !== lastTranscriptRef.current) {
      onTranscript?.(fullTranscript);
      lastTranscriptRef.current = fullTranscript;
    }
  }, [fullTranscript, onTranscript]);

  // Clear last transcript ref when recording starts
  useEffect(() => {
    if (isRecording) {
      lastTranscriptRef.current = "";
    }
  }, [isRecording]);

  const handleStart = useCallback(() => {
    clearTranscript();
    startRecording();
  }, [startRecording, clearTranscript]);

  const handleConfirm = useCallback(() => {
    stopRecording();
    onConfirm?.();
  }, [stopRecording, onConfirm]);

  const handleCancelRecording = useCallback(() => {
    stopRecording();
    clearTranscript();
    onCancel?.();
  }, [stopRecording, clearTranscript, onCancel]);

  // Don't render if not supported
  if (!isSupported) {
    return null;
  }

  // Recording interface - show histogram with controls
  if (isRecording) {
    return (
      <div className="flex items-center gap-1">
        <DictationVisualizer
          audioStream={audioStream}
          isRecording={isRecording}
          barColor="#a855f7"
          width={120}
          height={24}
          barWidth={2}
          gap={2}
        />

        {/* Cancel button */}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleCancelRecording}
                className={cn(
                  "h-8 w-8 p-1 rounded-md",
                  "bg-transparent",
                  "hover:bg-red-500/10 hover:text-red-500",
                  "text-zinc-500 dark:text-zinc-400",
                  "transition-all duration-200",
                  "focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0",
                  "[&_svg]:!size-5"
                )}
                aria-label="Cancel recording"
              >
                <X className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              Cancel
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Confirm button */}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleConfirm}
                className={cn(
                  "h-8 w-8 p-1 rounded-md",
                  "bg-transparent",
                  "hover:bg-green-500/10 hover:text-green-500",
                  "text-zinc-500 dark:text-zinc-400",
                  "transition-all duration-200",
                  "focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0",
                  "[&_svg]:!size-5"
                )}
                aria-label="Confirm recording"
              >
                <Check className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              Done
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {error && (
          <span className="text-xs text-red-500 ml-2">{error}</span>
        )}
      </div>
    );
  }

  // Idle state - show mic button
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleStart}
            disabled={disabled}
            className={cn(
              "h-8 w-8 p-1 rounded-md",
              "bg-transparent",
              "transition-all duration-200",
              "focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0",
              "ring-0 ring-offset-0 focus-visible:ring-offset-0",
              "disabled:opacity-50 disabled:hover:bg-transparent",
              "[&_svg]:!size-5",
              "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300",
              className
            )}
            aria-label="Start voice dictation"
          >
            <Mic className="h-5 w-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          Voice dictation
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

VoiceDictationButton.propTypes = {
  onTranscript: PropTypes.func,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default VoiceDictationButton;
